// Home.jsx - Página principal con video y cuadernillo modificado
import React, { useRef, useState, useEffect } from 'react';
import { FaHeart, FaPaw, FaChild, FaLeaf, FaPlay, FaArrowRight, FaBookOpen, FaDownload, FaPause, FaStop, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import VirtualZones from '../VirtualZones/VirtualZones';
import api from '../../services/api';

const Home = ({ setCurrentPage }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // API state
  const [pageContent, setPageContent] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);

  const cuadernilloImages = [
    '/images/gallery/componentes/feellab.jpeg',
    '/images/gallery/componentes/feellab1.jpeg',
    '/images/gallery/componentes/feellab2.jpeg'
  ];

  const features = [
    {
      icon: FaHeart,
      title: "En familia",
      description: "Desarrollamos la conexión emocional con los animales",
      color: "from-pink-400 to-rose-400"
    },
    {
      icon: FaPaw,
      title: "Con Amigos",
      description: "Aprendemos a respetar y proteger a nuestros amigos",
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: FaChild,
      title: "En la escuela",
      description: "Metodología especialmente diseñada para pequeños",
      color: "from-amber-500 to-orange-400"
    },
    {
      icon: FaLeaf,
      title: "Animal Friends",
      description: "Conectamos con el mundo natural que nos rodea",
      color: "from-teal-400 to-emerald-400"
    }
  ];

  // Cargar datos de la API
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await api.getContent();
        if (response?.success && response.data?.home) {
          setPageContent(response.data.home);
        }
      } catch (error) {
        console.error('Error cargando contenido:', error);
      }
    };

    const fetchTeam = async () => {
      try {
        const response = await api.getTeam();
        if (response?.success && Array.isArray(response.data)) {
          setTeamMembers(response.data);
        }
      } catch (error) {
        console.error('Error cargando equipo:', error);
      }
    };

    fetchContent();
    fetchTeam();
  }, []);

  const getContent = (section, field, fallback = '') => {
    return pageContent?.[section]?.[field] || fallback;
  };

  // Efectos para el video
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleLoadedMetadata = () => setDuration(video.duration);
      const handleTimeUpdate = () => setCurrentTime(video.currentTime);
      const handleEnded = () => { setIsPlaying(false); setCurrentTime(0); };

      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('ended', handleEnded);

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('ended', handleEnded);
      };
    }
  }, []);

  // Carrusel automático
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % cuadernilloImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, cuadernilloImages.length]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying) { video.pause(); setIsPlaying(false); }
      else { video.play(); setIsPlaying(true); }
    }
  };

  const stopVideo = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % cuadernilloImages.length);
  const prevImage = () => setCurrentImageIndex((prev) => prev === 0 ? cuadernilloImages.length - 1 : prev - 1);
  const goToImage = (index) => setCurrentImageIndex(index);
  const toggleAutoPlay = () => setIsAutoPlaying(!isAutoPlaying);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-sky-50 via-emerald-50 to-teal-50 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Contenido */}
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-white/80 rounded-full px-4 py-2 backdrop-blur-sm">
                <FaPaw className="text-sky-500" />
                <span className="text-sm font-medium text-gray-700">
                  {getContent('hero', 'subtitle', 'Laboratorio de Empatía')}
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-titulo font-bold text-gray-800 leading-tight">
                <span className="bg-gradient-to-r from-sky-500 to-emerald-500 bg-clip-text text-transparent">
                  {getContent('hero', 'title', 'Siente, crea y conecta')}
                </span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                {getContent('hero', 'content', 'Un espacio mágico donde los niños aprenden sobre emociones, empatía y el cuidado de los animales a través de experiencias únicas y divertidas. Mediada por las artes plásticas.')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => {
                    localStorage.setItem('showVideos', 'true');
                    setCurrentPage('gallery');
                  }}
                  className="group bg-gradient-to-r from-sky-500 to-emerald-500 text-white px-8 py-4 rounded-full font-medium text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <FaPlay />
                  <span>Experiencias FeelLab</span>
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </button>

                <button
                  onClick={() => setCurrentPage('about')}
                  className="bg-white text-gray-800 px-8 py-4 rounded-full font-medium text-lg shadow-lg hover:shadow-xl border border-gray-200 hover:bg-gray-50 transform hover:scale-105 transition-all duration-300"
                >
                  Conocer más
                </button>
              </div>
            </div>

            {/* Video de inicio */}
            <div className="relative">
              <div className="bg-gradient-to-br from-sky-100/50 to-emerald-100/50 rounded-3xl p-8 backdrop-blur-sm border border-white/30">
                <div className="aspect-video bg-black rounded-2xl shadow-lg overflow-hidden relative">
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    preload="metadata"
                    onError={() => console.error('Error cargando video')}
                  >
                    <source src="/videos/gallery/componentes/video-inicio.mp4" type="video/mp4" />
                    Tu navegador no soporta el elemento de video.
                  </video>

                  <div className="absolute inset-0 group/video">
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover/video:opacity-100 transition-opacity duration-300 p-4">
                      <div
                        className="w-full bg-white/30 rounded-full h-1 mb-3 cursor-pointer hover:h-2 transition-all"
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const percent = (e.clientX - rect.left) / rect.width;
                          const newTime = percent * duration;
                          if (videoRef.current) {
                            videoRef.current.currentTime = newTime;
                            setCurrentTime(newTime);
                          }
                        }}
                      >
                        <div
                          className="bg-white h-full rounded-full transition-all duration-300"
                          style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                        ></div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button onClick={togglePlayPause} className="bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition-colors">
                            {isPlaying ? <FaPause className="text-sm" /> : <FaPlay className="text-sm ml-0.5" />}
                          </button>
                          <button onClick={stopVideo} className="bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition-colors">
                            <FaStop className="text-sm" />
                          </button>
                          <span className="text-white text-sm">{formatTime(currentTime)} / {formatTime(duration)}</span>
                        </div>
                        <h3 className="text-white font-medium text-sm">Video de Inicio FeelLab</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 w-8 h-8 bg-amber-400 rounded-full animate-bounce"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-orange-400 rounded-full animate-bounce" style={{animationDelay: '0.5s'}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Sección del Cuadernillo */}
      <section className="py-16 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-white/80 rounded-full px-6 py-3 backdrop-blur-sm mb-6">
              <FaBookOpen className="text-emerald-500 text-xl" />
              <span className="font-medium text-gray-700">Material Educativo</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-titulo font-bold text-gray-800 mb-4">
              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                Cuadernillo FeelLab
              </span>
            </h2>
            
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Descarga nuestra guía completa con actividades, ejercicios y recursos 
              para implementar la metodología FeelLab en casa o en el aula.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Carrusel */}
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-xl p-4 transform hover:scale-105 transition-transform duration-300 relative overflow-hidden group">
                  <div className="relative aspect-[3/4] overflow-hidden rounded-xl">
                    <img
                      src={cuadernilloImages[currentImageIndex]}
                      alt={`Cuadernillo FeelLab - Imagen ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover transition-opacity duration-500"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center hidden">
                      <div className="text-center">
                        <FaBookOpen className="text-4xl text-emerald-500 mb-3 mx-auto" />
                        <p className="text-emerald-700 font-medium">Cuadernillo FeelLab</p>
                      </div>
                    </div>

                    <button onClick={prevImage} className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100">
                      <FaChevronLeft />
                    </button>
                    <button onClick={nextImage} className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100">
                      <FaChevronRight />
                    </button>

                    <div className="absolute top-3 left-3 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {cuadernilloImages.length}
                    </div>
                    <button
                      onClick={toggleAutoPlay}
                      className={`absolute top-3 right-3 px-3 py-1 rounded-full text-sm transition-colors ${isAutoPlaying ? 'bg-emerald-500 text-white' : 'bg-black/50 text-white hover:bg-black/70'}`}
                    >
                      {isAutoPlaying ? 'Auto' : 'Manual'}
                    </button>
                  </div>

                  <div className="flex justify-center space-x-2 mt-4">
                    {cuadernilloImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToImage(index)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${currentImageIndex === index ? 'bg-emerald-500 scale-110' : 'bg-gray-300 hover:bg-gray-400'}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="absolute -top-3 -right-3 w-6 h-6 bg-emerald-400 rounded-full animate-pulse"></div>
                <div className="absolute -bottom-3 -left-3 w-4 h-4 bg-teal-400 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
              </div>

              {/* Información y descarga */}
              <div className="space-y-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-emerald-100">
                  <h3 className="text-2xl font-titulo font-bold text-emerald-800 mb-4">
                    Guía Completa FeelLab
                  </h3>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                      <p className="text-gray-600">Actividades prácticas para desarrollar empatía animal</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                      <p className="text-gray-600">Ejercicios de expresión artística y emocional</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                      <p className="text-gray-600">Metodología paso a paso para educadores</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                      <p className="text-gray-600">Recursos adicionales y herramientas de evaluación</p>
                    </div>
                  </div>

                  <a
                    href="/Docs/Cuadernillo 1 FeelLab.pdf"
                    download="Cuadernillo 1 FeelLab.pdf"
                    className="inline-flex items-center space-x-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 w-full justify-center"
                  >
                    <FaDownload />
                    <span>Descargar Cuadernillo PDF</span>
                  </a>
                  
                  <p className="text-sm text-gray-500 text-center mt-3">
                    Formato PDF | Descarga gratuita
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Virtual Zones Section */}
      <VirtualZones />

      {/* Features Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-titulo font-bold text-gray-800 mb-4">
              {getContent('features_title', 'title', '¿Qué hacemos especial?')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {getContent('features_title', 'content', 'Nuestro laboratorio combina metodologías innovadoras con la magia de la conexión entre niños, animales y las artes plásticas.')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="group">
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100">
                    <div className={`w-14 h-14 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="text-white text-xl" />
                    </div>
                    <h3 className="text-xl font-titulo font-semibold text-gray-800 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-sky-500 to-emerald-500 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-titulo font-bold text-white mb-4">
            {getContent('cta', 'title', '¡Únete a nuestra comunidad!')}
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {getContent('cta', 'content', 'Descubre cómo podemos ayudar a tu hijo a desarrollar empatía y amor por los animales')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' }) & setTimeout(() => setCurrentPage('gallery'), 100)}
              className="bg-white text-sky-600 px-8 py-4 rounded-full font-medium text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Explorar galería
            </button>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' }) & setTimeout(() => setCurrentPage('contact'), 100)}
              className="border-2 border-white text-white px-8 py-4 rounded-full font-medium text-lg hover:bg-white hover:text-sky-600 transform hover:scale-105 transition-all duration-300"
            >
              Contactar
            </button>
          </div>
        </div>
      </section>

      {/* Sección de Autores */}
      <section className="py-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h3 className="text-2xl font-titulo font-bold text-gray-800 mb-6">Autores</h3>
            <div className="max-w-3xl mx-auto">
              {teamMembers.length > 0 ? (
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  {teamMembers.map((member) => (
                    <div key={member.id} className="bg-white rounded-lg p-6 shadow-md">
                      <h4 className="font-semibold text-gray-800 mb-2">{member.name}</h4>
                      <p className="text-sm text-gray-600">{member.role}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <h4 className="font-semibold text-gray-800 mb-2">Leydi Diana Saltarín Álvarez</h4>
                    <p className="text-sm text-gray-600"></p>
                  </div>
                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <h4 className="font-semibold text-gray-800 mb-2">Leidylizeth Hernandez Chajin</h4>
                    <p className="text-sm text-gray-600"></p>
                  </div>
                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <h4 className="font-semibold text-gray-800 mb-2">DR. Freddy Marín González</h4>
                    <p className="text-sm text-gray-600"></p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
